import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { t } from 'i18next'
import { ArrowDown, Check, Quote, X } from 'lucide-react'

import { triggerToast } from '@/components/Toast'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui'
import { Button } from '@/components/ui/button'
import { useGetCommentsList } from '@/features/annotation-management'
import { useDocumentStore } from '@/features/document'
import {
  useAddComment,
  useMarkCommentThreadResolved,
  useUpdateComment,
} from '@/features/view-tfl-uploaded/hooks/hooks.ts'
import { useCurrentUser } from '@/hooks/useAuth'
import { ResponseThread, ThreadActionableType } from '@/schema'
import { cn, formatFullName } from '@/utils'
import { getUserInitials } from '@/utils/avatar'
import { TABLE_PAGE_SIZE } from '@/utils/constants'

import { useAuthStore } from '../../../auth'
import { ERoleType } from '../../../user-management'
import { useCommentStore, useTflViewerStore, useThreadStore } from '../../store'
import { ECommentStatus } from '../../types'
import { extractMentionedUserIds } from '../../utils/mentionUtils'
import { ExpandableCommentText } from '../CommentSidebar/ExpandableCommentText'
import { TeamworkSyncStatus } from '../TeamworkSyncStatus'

import {
  CommentStatus,
  DeleteCommentDialog,
  ThreadCommentItem,
  ThreadReplyFooter,
  ThreadReplySeparator,
} from './index'

const SCROLL_THRESHOLD = 50

export function CommentDetail() {
  const { activeDocumentId, getIsLatestVersion } = useDocumentStore()
  const { id } = useParams<{ id: string }>()
  const getListDLViewerHasAccess = useAuthStore(
    (state) => state.getListDLViewerHasAccess
  )

  const getListPrjProjectLeadHasAccess = useAuthStore(
    (state) => state.getListPrjProjectLeadHasAccess
  )

  const listProjectProjectLeadHasAccess = getListPrjProjectLeadHasAccess()

  const listDLViewerHasAccess = getListDLViewerHasAccess() as string[]

  const isViewer = listDLViewerHasAccess.includes(id as string)

  const isLatestVersion = activeDocumentId
    ? getIsLatestVersion(activeDocumentId)
    : true
  const {
    commentsByDocVersion,
    activeCommentId,
    showCommentDetail,
    editingComment,
    quotedCommentId,
    setActiveCommentId,
    setShowCommentDetail,
    setEditingComment,
    setDeletingThread,
    setDeleteDialogOpen,
    setQuotedComment,
    targetReplyId,
    setTargetReplyId,
  } = useCommentStore()
  const { metadataByDocId } = useTflViewerStore()

  const { data: currentUser } = useCurrentUser()
  const {
    commentsByThreadId,
    setThread,
    updateCommentInThread,
    setDeletingComment,
    setDeleteCommentDialogOpen,
  } = useThreadStore()
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [copiedCommentId, setCopiedCommentId] = useState<string | null>(null)
  const [showLatestRepliesBtn, setShowLatestRepliesBtn] = useState(false)
  const [commentRoot, setCommentRoot] = useState<ResponseThread | null>(null)
  const [showOlderReplies, setShowOlderReplies] = useState(false)
  const isOrgAdmin = currentUser?.roles?.some(
    (r) => r.role_name === ERoleType.ORGANIZATION_ADMIN
  )
  const isProjectLeadOfProject = useMemo(
    () =>
      commentRoot?.project?.id
        ? listProjectProjectLeadHasAccess.includes(commentRoot?.project.id)
        : false,
    [commentRoot?.project, listProjectProjectLeadHasAccess]
  )

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const documentVersionId = useMemo(
    () => metadataByDocId[activeDocumentId || '']?.version_id || '',
    [activeDocumentId, metadataByDocId]
  )
  const thread = useMemo(
    () => commentsByDocVersion[documentVersionId]?.[activeCommentId || ''],
    [activeCommentId, commentsByDocVersion, documentVersionId]
  )
  const threadId = useMemo(() => thread?.thread_id, [thread?.thread_id])

  const {
    data: { pages = [] } = {},
    isLoading,
    error,
    refetch: refetchComments,
  } = useGetCommentsList({ threadId })

  const comments = useMemo(() => {
    return pages?.flatMap((page) => page?.data || []) || null
  }, [pages])

  const isTotalReplies = useMemo(() => {
    return (pages?.[0]?.pagination?.total_items ?? 1) - 1 // -1 for the root comment
  }, [pages])

  const markAsResolvedMutation = useMarkCommentThreadResolved(
    threadId || '',
    documentVersionId
  )

  const { mutate: addComment, isPending: isSavingComment } = useAddComment()
  const { mutate: updateComment, isPending: isUpdatingComment } =
    useUpdateComment(documentVersionId)

  const activeThreadComments = useMemo(() => {
    return threadId ? commentsByThreadId[threadId] : null
  }, [commentsByThreadId, threadId])

  const replies = useMemo(
    () =>
      commentRoot?.id && activeThreadComments
        ? activeThreadComments.filter((reply) => reply.id !== thread?.id)
        : [],
    [activeThreadComments, commentRoot?.id, thread?.id]
  )

  // Split replies into latest and older
  const { latestReplies, olderReplies } = useMemo(() => {
    if (replies.length <= TABLE_PAGE_SIZE) {
      return { latestReplies: replies, olderReplies: [] }
    }
    return {
      latestReplies: replies.slice(0, TABLE_PAGE_SIZE),
      olderReplies: replies.slice(TABLE_PAGE_SIZE, replies.length),
    }
  }, [replies])

  const isStatusOpen = useMemo(
    () => thread?.thread_status === ECommentStatus.OPEN,
    [thread?.thread_status]
  )
  const isStatusResolved = useMemo(
    () => thread?.thread_status === ECommentStatus.RESOLVED,
    [thread?.thread_status]
  )

  useEffect(() => {
    if (comments && comments.length > 0 && threadId) {
      setThread(threadId, comments)
    }
  }, [comments, threadId, setThread])

  // Scroll detection to show "Latest replies" button
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer
      const hasOverflow = scrollHeight > clientHeight + 8

      if (!hasOverflow) {
        setShowLatestRepliesBtn(false)
        return
      }

      const isAtBottom =
        scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD
      setShowLatestRepliesBtn(!isAtBottom)
    }

    handleScroll()

    scrollContainer.addEventListener('scroll', handleScroll)
    return () => scrollContainer.removeEventListener('scroll', handleScroll)
  }, [replies.length, thread?.thread_status])

  useEffect(() => {
    if (thread) setCommentRoot({ ...thread })
  }, [thread])

  useEffect(() => {
    if (editingComment) {
      // Set active comment and show detail
      setActiveCommentId(editingComment.id)
      setShowCommentDetail(true)
      // Open edit mode
      handleEditComment(editingComment.id)
      // Reset editing comment
      setEditingComment(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingComment])

  const handleFetchLatestReplies = async () => {
    await refetchComments()
    // Scroll to bottom after fetching
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight
    }
  }

  // Check if current user is the author of the root comment
  const isAuthor = useMemo(
    () =>
      currentUser && commentRoot
        ? currentUser.id === commentRoot.created_by
        : false,
    [currentUser, commentRoot]
  )
  const isOrgAdminOrProjectLeadOrAuthor = useMemo(
    () => isOrgAdmin || isProjectLeadOfProject || isAuthor,
    [isOrgAdmin, isProjectLeadOfProject, isAuthor]
  )

  const handleCopyComment = (commentId: string, content: string) => {
    navigator.clipboard.writeText(content)
    setCopiedCommentId(commentId)

    setTimeout(() => {
      setCopiedCommentId(null)
    }, 2000)
  }

  const handleEditComment = (commentId: string) => {
    setEditingCommentId(commentId)
  }

  const handleCancelEdit = () => {
    setEditingCommentId(null)
  }

  const handleSubmitEdit = (commentId: string, newContent: string) => {
    if (!threadId) return

    const mentionedUserIds = extractMentionedUserIds(newContent)

    updateComment(
      {
        commentId,
        data: {
          content: newContent,
          mentioned_user_ids: mentionedUserIds,
        },
      },
      {
        onSuccess: (response) => {
          const isThreadRoot = response?.data[0]?.id == commentRoot?.id
          if (isThreadRoot) {
            setCommentRoot((prev) =>
              prev
                ? {
                    ...prev,
                    content: response.data[0].content,
                    content_last_edited_at:
                      response.data[0].content_last_edited_at,
                  }
                : response.data[0]
            )
          } else {
            updateCommentInThread(threadId, response.data[0])
          }
          setEditingCommentId(null)
          triggerToast('success', t('tflViewer.comment.messages.updateSuccess'))
        },
        onError: () => {
          triggerToast('error', t('tflViewer.comment.messages.updateError'))
        },
      }
    )
  }

  const handleDeleteComment = (commentId: string) => {
    if (!threadId) return

    setDeletingComment({
      commentId,
      threadId,
      documentVersionId,
    })
    setDeleteCommentDialogOpen(true)
  }

  const handleSubmitReply = (content: string) => {
    if (!commentRoot || !threadId || !currentUser) return

    // Scroll to bottom to show new reply
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    })

    const mentionedUserIds = extractMentionedUserIds(content)

    // Then call API
    addComment(
      {
        content: content,
        document_version_id: documentVersionId,
        parent_comment_id: commentRoot.id,
        quote_comment_id: quotedCommentId || undefined,
        mentioned_user_ids: mentionedUserIds,
      },
      {
        onSuccess: () => {
          triggerToast('success', t('tflViewer.addCommentDialog.toast.success'))
          refetchComments()
          setQuotedComment(null)
        },
        onError: () => {
          triggerToast('error', t('tflViewer.addCommentDialog.toast.error'))
        },
      }
    )
  }

  const handleMarkAsResolved = () => {
    markAsResolvedMutation.mutate('resolved', {
      onSuccess: () => {
        triggerToast('success', t('tflViewer.comment.messages.resolveSuccess'))
      },
      onError: () => {
        triggerToast('error', t('tflViewer.comment.messages.errorGeneric'))
      },
    })
  }

  const handleReopenThread = () => {
    if (isStatusOpen) return

    markAsResolvedMutation.mutate('open', {
      onSuccess: () => {
        triggerToast('success', t('tflViewer.comment.messages.reopenSuccess'))
      },
      onError: () => {
        triggerToast('error', t('tflViewer.comment.messages.errorGeneric'))
      },
    })
  }

  const handleClose = () => {
    setActiveCommentId(undefined)
    setShowCommentDetail(false)
    setQuotedComment(null)
    setEditingCommentId(null)
    setShowOlderReplies(false)
    setTargetReplyId(null)
  }

  const isVisible = useMemo(
    () => !!(activeCommentId && showCommentDetail && activeDocumentId),
    [activeCommentId, activeDocumentId, showCommentDetail]
  )

  useEffect(() => {
    if (commentRoot && isVisible) {
      refetchComments()

      if (scrollContainerRef.current) {
        requestAnimationFrame(() => {
          const el = scrollContainerRef.current
          if (el) {
            el.scrollTop = el.scrollHeight
          }
        })
      }
    }
  }, [commentRoot, isVisible, refetchComments])

  const handleDeleteCommentThread = () => {
    if (!threadId) return

    setDeletingThread({ threadId, documentVersionId })
    setDeleteDialogOpen(true)
  }

  const handleQuoteComment = (commentId: string) => {
    setQuotedComment(commentId)
  }

  const quotedComment = useMemo(
    () => activeThreadComments?.find((c) => c.id === quotedCommentId),
    [quotedCommentId, activeThreadComments]
  )

  // Auto-scroll to target reply
  useEffect(() => {
    if (!targetReplyId || !isVisible || !replies.length) return

    // Ensure it's rendered by opening older replies if needed
    const isTargetInOlder = olderReplies.some((r) => r.id === targetReplyId)
    if (isTargetInOlder && !showOlderReplies) {
      setShowOlderReplies(true)
    }

    // Scroll after a small delay to allow for rendering/expansion
    const timeoutIds = [
      setTimeout(
        () => {
          const replyElement = document.getElementById(targetReplyId)
          if (replyElement) {
            replyElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

            // Add a temporary highlight class => might remove
            replyElement.classList.add(
              'bg-blue-50',
              'transition-colors',
              'duration-500'
            )
            setTimeout(() => {
              replyElement.classList.remove('bg-blue-50')
            }, 2000)

            setTargetReplyId(null)
          }
        },
        isTargetInOlder ? 300 : 100
      ),
      // Fallback in case list is still rendering
      setTimeout(() => {
        const replyElement = document.getElementById(targetReplyId)
        if (replyElement && targetReplyId) {
          replyElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTargetReplyId(null)
        }
      }, 1000),
    ]

    return () => timeoutIds.forEach(clearTimeout)
  }, [
    targetReplyId,
    isVisible,
    replies,
    olderReplies,
    showOlderReplies,
    setTargetReplyId,
  ])

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex',
        isVisible ? '' : '-z-10 invisible'
      )}
    >
      {/* Backdrop - click to close */}
      <button
        type="button"
        className={cn(
          'flex-1 shadow-sm cursor-pointer',
          isVisible ? '' : 'hidden'
        )}
        onClick={handleClose}
        aria-label="Close comment detail"
        tabIndex={0}
        style={{
          outline: 'none',
          border: 'none',
          background: 'transparent',
          padding: 0,
        }}
      />

      {/* Right drawer */}
      <aside
        className={cn(
          'flex h-screen md:w-[400px] flex-col border-l border-secondary bg-secondary-alt overflow-hidden border-solid border-y-0 border-r-0 transition-transform duration-300',
          isVisible ? '' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-center justify-between px-6 py-4 shadow-sm bg-primary',
            'border border-solid border-t-0 border-x-0 border-secondary'
          )}
        >
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 m-0">
              Comment details
            </h2>
            <CommentStatus
              threadStatus={thread?.thread_status}
              isViewer={isViewer}
              isLatestVersion={isLatestVersion}
              isOrgAdminOrProjectLeadOrAuthor={isOrgAdminOrProjectLeadOrAuthor}
              onReopenThread={handleReopenThread}
              onMarkAsResolved={handleMarkAsResolved}
            />
          </div>

          <div className="flex gap-2 items-center justify-center">
            {thread?.actionable_type === ThreadActionableType.ACTIONABLE && (
              <>
                <TeamworkSyncStatus thread={thread} />
                <div className="h-4 border-r border-y-0 border-l-0 border-secondary border-solid ml-2" />
              </>
            )}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="text"
                  className="size-6 p-0 hover:bg-gray-50 flex-shrink-0 group"
                  onClick={handleClose}
                  title="Close thread"
                  aria-label="Close thread"
                >
                  <X className="size-5 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <span>Close</span>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-1 items-center justify-center p-8">
            <div className="text-center">
              <p className="text-gray-600">Loading thread...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex flex-1 items-center justify-center p-8">
            <div className="text-center">
              <p className="text-red-600">Failed to load thread</p>
              <Button onClick={handleClose} className="mt-4" variant="outlined">
                Close
              </Button>
            </div>
          </div>
        )}

        {!isLoading && !error && !commentRoot && (
          <div className=" relative flex flex-1 items-center justify-center p-8">
            <div className="text-center">
              <p className="text-gray-600">Thread not found</p>
              <Button onClick={handleClose} className="mt-4" variant="outlined">
                Close
              </Button>
            </div>
          </div>
        )}

        {!isLoading && !error && commentRoot && (
          <div className="flex flex-1 min-h-0 flex-col">
            {/* Scrollable content */}
            <div className="relative flex-1 min-h-0">
              <div ref={scrollContainerRef} className="h-full overflow-y-auto">
                {/* Anchor thread to bottom but allow overflow */}
                <div className="flex min-h-full flex-col justify-start pb-4">
                  <ThreadCommentItem
                    comment={commentRoot}
                    commentRoot={thread}
                    isRoot
                    isViewer={isViewer}
                    isEditing={editingCommentId === commentRoot.id}
                    isCopied={copiedCommentId === commentRoot.id}
                    onCopy={() =>
                      handleCopyComment(commentRoot.id, commentRoot.content)
                    }
                    onEdit={() => handleEditComment(commentRoot.id)}
                    onDelete={handleDeleteCommentThread}
                    onCancelEdit={handleCancelEdit}
                    onSubmitEdit={(newContent) =>
                      handleSubmitEdit(commentRoot.id, newContent)
                    }
                    onQuoteComment={() => handleQuoteComment(commentRoot.id)}
                    isPending={isUpdatingComment}
                    threadStatus={thread?.thread_status || ''}
                    isLatestVersion={isLatestVersion}
                  />

                  {replies.length > 0 && (
                    <>
                      <ThreadReplySeparator count={isTotalReplies} />

                      <div className="flex flex-col-reverse">
                        {/* Latest replies - always visible */}
                        {latestReplies.map((reply) => {
                          // Skip the root comment
                          if (reply.id === thread?.id) return null

                          // Find the quoted comment
                          const quotedComment = activeThreadComments?.find(
                            (c) => c.id === reply.quote_comment_id
                          )

                          return (
                            <ThreadCommentItem
                              key={reply.id}
                              comment={reply}
                              isRoot={false}
                              isViewer={isViewer}
                              isEditing={editingCommentId === reply.id}
                              isCopied={copiedCommentId === reply.id}
                              onCopy={() =>
                                handleCopyComment(reply.id, reply.content)
                              }
                              onEdit={() => handleEditComment(reply.id)}
                              onDelete={() => handleDeleteComment(reply.id)}
                              onCancelEdit={handleCancelEdit}
                              onSubmitEdit={(newContent) =>
                                handleSubmitEdit(reply.id, newContent)
                              }
                              onQuoteComment={() =>
                                handleQuoteComment(reply.id)
                              }
                              isPending={isUpdatingComment}
                              threadStatus={thread?.thread_status || ''}
                              isLatestVersion={isLatestVersion}
                              quotedComment={quotedComment}
                            />
                          )
                        })}

                        {/* Older replies - expandable/collapsible */}
                        {olderReplies.length > 0 && (
                          <Collapsible
                            open={showOlderReplies}
                            onOpenChange={setShowOlderReplies}
                          >
                            <CollapsibleTrigger asChild>
                              <button
                                type="button"
                                className={cn(
                                  'flex w-full items-center justify-center gap-2 py-3 px-4 text-xs text-gray-500 hover:bg-gray-50 transition-colors border-none bg-transparent cursor-pointer',
                                  'group'
                                )}
                                aria-label={
                                  showOlderReplies
                                    ? 'Collapse older replies'
                                    : `Expand ${olderReplies.length} older replies`
                                }
                              >
                                <div
                                  className={cn(
                                    'border border-solid border-x-0 border-secondary h-2.5 flex-1',
                                    showOlderReplies
                                      ? 'h-px border-t-0'
                                      : 'h-2.5'
                                  )}
                                />
                                <div className="text-sm text-secondary-700 py-md px-lg border border-primary border-solid rounded-md bg-white mx-1">
                                  {olderReplies.length}
                                </div>
                                <div
                                  className={cn(
                                    'border border-solid border-x-0 border-secondary flex-1',
                                    showOlderReplies
                                      ? 'h-px border-t-0'
                                      : 'h-2.5'
                                  )}
                                />
                              </button>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="flex flex-col-reverse">
                                <div className="px-4 py-3 w-full">
                                  <div
                                    className={cn(
                                      'border border-solid border-x-0 border-secondary border-t-0 flex-1',
                                      'h-px'
                                    )}
                                  />
                                </div>
                                {olderReplies.map((reply) => {
                                  // Skip the root comment
                                  if (reply.id === thread?.id) return null

                                  // Find the quoted comment
                                  const quotedComment =
                                    activeThreadComments?.find(
                                      (c) => c.id === reply.quote_comment_id
                                    )

                                  return (
                                    <ThreadCommentItem
                                      key={reply.id}
                                      comment={reply}
                                      isRoot={false}
                                      isViewer={isViewer}
                                      isEditing={editingCommentId === reply.id}
                                      isCopied={copiedCommentId === reply.id}
                                      onCopy={() =>
                                        handleCopyComment(
                                          reply.id,
                                          reply.content
                                        )
                                      }
                                      onEdit={() => handleEditComment(reply.id)}
                                      onDelete={() =>
                                        handleDeleteComment(reply.id)
                                      }
                                      onCancelEdit={handleCancelEdit}
                                      onSubmitEdit={(newContent) =>
                                        handleSubmitEdit(reply.id, newContent)
                                      }
                                      onQuoteComment={() =>
                                        handleQuoteComment(reply.id)
                                      }
                                      isPending={isUpdatingComment}
                                      threadStatus={thread?.thread_status || ''}
                                      isLatestVersion={isLatestVersion}
                                      quotedComment={quotedComment}
                                    />
                                  )
                                })}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {showLatestRepliesBtn && (
                <button
                  onClick={handleFetchLatestReplies}
                  className={cn(
                    'absolute border-solid bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border border-blue-200 bg-primary px-2.5 py-1 text-xs font-medium text-blue-600 shadow-none cursor-pointer hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40'
                  )}
                >
                  <ArrowDown className="size-4 text-blue-600" />
                  Latest replies
                </button>
              )}

              {copiedCommentId && (
                <Badge
                  className={cn(
                    'absolute border-solid bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border border-blue-200 bg-primary px-2.5 py-1 text-xs font-medium text-blue-600 shadow-none cursor-pointer hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40'
                  )}
                >
                  <Check className="h-4 w-4 text-blue-600" />
                  Copied
                </Badge>
              )}
            </div>

            <div className="flex flex-col">
              {quotedComment && (
                <div className="px-5">
                  <div className="px-0.5 py-0.5 bg-white border border-b-0 border-solid border-primary rounded-md rounded-b-none">
                    <div className="flex flex-col gap-2 rounded-sm border border-solid border-white bg-gray-100 px-0.5 py-1">
                      <div className="flex gap-1 items-center">
                        <Quote className="size-3" />

                        <Avatar className="size-6 border border-solid border-white">
                          <AvatarImage
                            src={
                              quotedComment.created_by_user?.avatar_url ?? ''
                            }
                            alt={formatFullName(
                              quotedComment.created_by_user?.first_name ?? '',
                              quotedComment.created_by_user?.last_name ?? ''
                            )}
                          />
                          <AvatarFallback className="text-xs">
                            {quotedComment.created_by_user
                              ? getUserInitials(quotedComment.created_by_user)
                              : ''}
                          </AvatarFallback>
                        </Avatar>

                        <div className="text-sm font-medium">
                          {formatFullName(
                            quotedComment.created_by_user?.first_name ?? '',
                            quotedComment.created_by_user?.last_name ?? ''
                          )}
                        </div>

                        <Button
                          variant="text"
                          className="size-6 p-0 hover:bg-gray-50 flex-shrink-0 group ml-auto"
                          onClick={() => setQuotedComment(null)}
                          title="Remove quote"
                          aria-label="Remove quote"
                        >
                          <X className="size-3 text-foreground-fg-quaternary-400 group-hover:text-quaternary-500" />
                        </Button>
                      </div>
                      <ExpandableCommentText
                        content={quotedComment.content}
                        isAllowExpandable={false}
                      />
                    </div>
                  </div>
                </div>
              )}
              {!isStatusResolved && isLatestVersion && !isViewer && (
                <ThreadReplyFooter
                  key={`reply-${activeCommentId}`}
                  onSubmit={handleSubmitReply}
                  isSubmitting={isSavingComment}
                  isQuoted={!!quotedComment}
                />
              )}
            </div>
          </div>
        )}
      </aside>

      <DeleteCommentDialog />
    </div>
  )
}
